<script setup>
import { h, computed, markRaw, onMounted, watch } from 'vue';
import { provideSidebarContext } from './provider';
import { useAccount } from 'dashboard/composables/useAccount';
import { useKbd } from 'dashboard/composables/utils/useKbd';
import { useMapGetter } from 'dashboard/composables/store';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useStorage } from '@vueuse/core';
import { useRoute } from 'vue-router';
import { useSidebarKeyboardShortcuts } from './useSidebarKeyboardShortcuts';
import { vOnClickOutside } from '@vueuse/components';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

import Button from 'dashboard/components-next/button/Button.vue';
import SidebarGroup from './SidebarGroup.vue';
import SidebarProfileMenu from './SidebarProfileMenu.vue';
import SidebarChangelogCard from './SidebarChangelogCard.vue';
import ChannelLeaf from './ChannelLeaf.vue';
import ComposeConversation from 'dashboard/components-next/NewConversation/ComposeConversation.vue';
import Logo from 'dashboard/components-next/icon/Logo.vue';
import Home from '@primeicons/vue/home';
import Search from '@primeicons/vue/search';
import Calendar from '@primeicons/vue/calendar';
import BuildingColumns from '@primeicons/vue/building-columns';
import ChartBar from '@primeicons/vue/chart-bar';
import CheckCircle from '@primeicons/vue/check-circle';
import Cog from '@primeicons/vue/cog';
import Comment from '@primeicons/vue/comment';
import Comments from '@primeicons/vue/comments';
import Inbox from '@primeicons/vue/inbox';
import Star from '@primeicons/vue/star';
import Tags from '@primeicons/vue/tags';
import Users from '@primeicons/vue/users';
import WavePulse from '@primeicons/vue/wave-pulse';

const props = defineProps({
  isMobileSidebarOpen: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  'closeKeyShortcutModal',
  'openKeyShortcutModal',
  'closeMobileSidebar',
]);

const primeIcon = icon => markRaw(icon);

const SIDEBAR_ICONS = {
  prospecting: primeIcon(Search),
  overview: primeIcon(Home),
  inbox: primeIcon(Inbox),
  chat: primeIcon(Comments),
  internalChat: primeIcon(Comment),
  calendar: primeIcon(Calendar),
  crm: primeIcon(ChartBar),
  funnel: primeIcon(WavePulse),
  companies: primeIcon(BuildingColumns),
  activities: primeIcon(CheckCircle),
  reports: primeIcon(ChartBar),
  goals: primeIcon(Star),
  audit: primeIcon(CheckCircle),
  products: primeIcon(Inbox),
  automations: primeIcon(WavePulse),
  settings: primeIcon(Cog),
  contacts: primeIcon(Users),
  labels: primeIcon(Tags),
};

const {
  accountScopedRoute,
  currentAccount,
  isCloudFeatureEnabled,
  isOnChatwootCloud,
} = useAccount();
const store = useStore();
const route = useRoute();
const searchShortcut = useKbd([`$mod`, 'k']);
const { t } = useI18n();

const isACustomBrandedInstance = useMapGetter(
  'globalConfig/isACustomBrandedInstance'
);

const globalConfig = useMapGetter('globalConfig/get');

const toggleShortcutModalFn = show => {
  if (show) {
    emit('openKeyShortcutModal');
  } else {
    emit('closeKeyShortcutModal');
  }
};

useSidebarKeyboardShortcuts(toggleShortcutModalFn);

// We're using localStorage to store the expanded item in the sidebar
// This helps preserve context when navigating between portal and dashboard layouts
// and also when the user refreshes the page
const expandedItem = useStorage(
  'next-sidebar-expanded-item',
  null,
  sessionStorage
);

const setExpandedItem = name => {
  expandedItem.value = expandedItem.value === name ? null : name;
};
provideSidebarContext({
  expandedItem,
  setExpandedItem,
});

const inboxes = useMapGetter('inboxes/getInboxes');
const labels = useMapGetter('labels/getLabelsOnSidebar');
const teams = useMapGetter('teams/getMyTeams');
const contactCustomViews = useMapGetter('customViews/getContactCustomViews');
const conversationCustomViews = useMapGetter(
  'customViews/getConversationCustomViews'
);
const isInternalChatEnabled = computed(() => {
  if (!currentAccount.value.id) return false;

  const planSettings = currentAccount.value.custom_attributes || {};
  if (!planSettings.plan_name) return true;

  return planSettings.yeszap_plan_features?.includes(
    FEATURE_FLAGS.INTERNAL_CHAT
  );
});

onMounted(() => {
  store.dispatch('labels/get');
  store.dispatch('inboxes/get');
  store.dispatch('notifications/unReadCount');
  store.dispatch('teams/get');
  store.dispatch('attributes/get');
  store.dispatch('customViews/get', 'conversation');
  store.dispatch('customViews/get', 'contact');
});

watch(
  isInternalChatEnabled,
  isEnabled => {
    if (!isEnabled) return;

    store.dispatch('internalChat/fetchThreads').catch(() => null);
  },
  { immediate: true }
);

const sortedInboxes = computed(() =>
  inboxes.value.slice().sort((a, b) => a.name.localeCompare(b.name))
);

const closeMobileSidebar = () => {
  if (!props.isMobileSidebarOpen) return;
  emit('closeMobileSidebar');
};

const menuItems = computed(() => {
  return [
    {
      name: 'Overview',
      label: t('SIDEBAR.REPORTS_OVERVIEW'),
      icon: SIDEBAR_ICONS.overview,
      to: accountScopedRoute('account_overview_reports'),
      activeOn: [
        'live_reports',
        'conversation_reports',
        'csat_reports',
        'agent_reports_index',
        'agent_reports_show',
        'agent_reports',
        'inbox_reports_index',
        'inbox_reports_show',
        'inbox_reports',
        'team_reports_index',
        'team_reports_show',
        'team_reports',
        'label_reports_index',
        'label_reports_show',
        'label_reports',
      ],
    },
    {
      name: 'Internal Chat',
      label: t('SIDEBAR.INTERNAL_CHAT'),
      icon: SIDEBAR_ICONS.internalChat,
      to: accountScopedRoute('internal_chat'),
      activeOn: ['internal_chat'],
      getterKeys: {
        count: 'internalChat/getUnreadCount',
      },
    },
    {
      name: 'Conversation',
      label: t('SIDEBAR.CONVERSATIONS'),
      icon: SIDEBAR_ICONS.chat,
      children: [
        {
          name: 'All',
          label: t('SIDEBAR.ALL_CONVERSATIONS'),
          activeOn: ['inbox_conversation'],
          to: accountScopedRoute('home'),
        },
        {
          name: 'Mentions',
          label: t('SIDEBAR.MENTIONED_CONVERSATIONS'),
          activeOn: ['conversation_through_mentions'],
          to: accountScopedRoute('conversation_mentions'),
        },
        {
          name: 'Unattended',
          activeOn: ['conversation_through_unattended'],
          label: t('SIDEBAR.UNATTENDED_CONVERSATIONS'),
          to: accountScopedRoute('conversation_unattended'),
        },
        {
          name: 'Scheduled',
          activeOn: ['scheduled_messages'],
          label: t('SIDEBAR.SCHEDULED_MESSAGES'),
          to: accountScopedRoute('scheduled_messages'),
        },
        {
          name: 'Folders',
          label: t('SIDEBAR.CUSTOM_VIEWS_FOLDER'),
          icon: SIDEBAR_ICONS.inbox,
          activeOn: ['conversations_through_folders'],
          children: conversationCustomViews.value.map(view => ({
            name: `${view.name}-${view.id}`,
            label: view.name,
            to: accountScopedRoute('folder_conversations', { id: view.id }),
          })),
        },
        {
          name: 'Teams',
          label: t('SIDEBAR.TEAMS'),
          icon: SIDEBAR_ICONS.contacts,
          activeOn: ['conversations_through_team'],
          children: teams.value.map(team => ({
            name: `${team.name}-${team.id}`,
            label: team.name,
            to: accountScopedRoute('team_conversations', { teamId: team.id }),
          })),
        },
        {
          name: 'Channels',
          label: t('SIDEBAR.CHANNELS'),
          icon: SIDEBAR_ICONS.inbox,
          activeOn: ['conversation_through_inbox'],
          children: sortedInboxes.value.map(inbox => ({
            name: `${inbox.name}-${inbox.id}`,
            label: inbox.name,
            to: accountScopedRoute('inbox_dashboard', { inbox_id: inbox.id }),
            component: leafProps =>
              h(ChannelLeaf, {
                label: leafProps.label,
                active: leafProps.active,
                inbox,
              }),
          })),
        },
        {
          name: 'Labels',
          label: t('SIDEBAR.LABELS'),
          icon: SIDEBAR_ICONS.labels,
          activeOn: ['conversations_through_label'],
          children: labels.value.map(label => ({
            name: `${label.title}-${label.id}`,
            label: label.title,
            icon: h('span', {
              class: `size-[12px] ring-1 ring-n-alpha-1 dark:ring-white/20 ring-inset rounded-sm`,
              style: { backgroundColor: label.color },
            }),
            to: accountScopedRoute('label_conversations', {
              label: label.title,
            }),
          })),
        },
      ],
    },
    {
      name: 'Calendar',
      label: t('SIDEBAR.CALENDAR'),
      icon: SIDEBAR_ICONS.calendar,
      to: accountScopedRoute('calendar'),
      activeOn: ['calendar'],
    },
    ...(isCloudFeatureEnabled(FEATURE_FLAGS.CRM)
      ? [
          {
            name: 'YesCRM',
            label: t('SIDEBAR.YESCRM'),
            icon: SIDEBAR_ICONS.crm,
            to: accountScopedRoute('yescrm_dashboard'),
            activeOn: [
              'yescrm_board',
              'yescrm_deal_detail',
              'yescrm_products',
              'yescrm_activities',
              'yescrm_goals_commissions',
              'yescrm_automations',
              'yescrm_audit',
              'yescrm_reports',
            ],
          },
          {
            name: 'Prospecting',
            label: t('PROSPECTING.TITLE'),
            icon: SIDEBAR_ICONS.prospecting,
            to: accountScopedRoute('prospecting'),
          },
        ]
      : []),
    {
      name: 'Contacts',
      label: t('SIDEBAR.CONTACTS'),
      icon: SIDEBAR_ICONS.contacts,
      children: [
        {
          name: 'All Contacts',
          label: t('SIDEBAR.ALL_CONTACTS'),
          to: accountScopedRoute(
            'contacts_dashboard_index',
            {},
            { page: 1, search: undefined }
          ),
          activeOn: ['contacts_dashboard_index', 'contacts_edit'],
        },
        {
          name: 'Active',
          label: t('SIDEBAR.ACTIVE'),
          to: accountScopedRoute('contacts_dashboard_active'),
          activeOn: ['contacts_dashboard_active'],
        },
        {
          name: 'Segments',
          icon: SIDEBAR_ICONS.contacts,
          label: t('SIDEBAR.CUSTOM_VIEWS_SEGMENTS'),
          children: contactCustomViews.value.map(view => ({
            name: `${view.name}-${view.id}`,
            label: view.name,
            to: accountScopedRoute(
              'contacts_dashboard_segments_index',
              { segmentId: view.id },
              { page: 1 }
            ),
            activeOn: [
              'contacts_dashboard_segments_index',
              'contacts_edit_segment',
            ],
          })),
        },
        {
          name: 'Tagged With',
          icon: SIDEBAR_ICONS.labels,
          label: t('SIDEBAR.TAGGED_WITH'),
          children: labels.value.map(label => ({
            name: `${label.title}-${label.id}`,
            label: label.title,
            icon: h('span', {
              class: `size-[12px] ring-1 ring-n-alpha-1 dark:ring-white/20 ring-inset rounded-sm`,
              style: { backgroundColor: label.color },
            }),
            to: accountScopedRoute(
              'contacts_dashboard_labels_index',
              { label: label.title },
              { page: 1, search: undefined }
            ),
            activeOn: [
              'contacts_dashboard_labels_index',
              'contacts_edit_label',
            ],
          })),
        },
      ],
    },
    {
      name: 'Companies',
      label: t('SIDEBAR.COMPANIES'),
      icon: SIDEBAR_ICONS.companies,
      to: accountScopedRoute(
        'companies_dashboard_index',
        {},
        { page: 1, search: undefined }
      ),
      activeOn: ['companies_dashboard_index'],
    },
    {
      name: 'Portals',
      label: t('SIDEBAR.HELP_CENTER.TITLE'),
      icon: SIDEBAR_ICONS.inbox,
      children: [
        {
          name: 'Articles',
          label: t('SIDEBAR.HELP_CENTER.ARTICLES'),
          activeOn: [
            'portals_articles_index',
            'portals_articles_new',
            'portals_articles_edit',
          ],
          to: accountScopedRoute('portals_index', {
            navigationPath: 'portals_articles_index',
          }),
        },
        {
          name: 'Categories',
          label: t('SIDEBAR.HELP_CENTER.CATEGORIES'),
          activeOn: [
            'portals_categories_index',
            'portals_categories_articles_index',
            'portals_categories_articles_edit',
          ],
          to: accountScopedRoute('portals_index', {
            navigationPath: 'portals_categories_index',
          }),
        },
        {
          name: 'Locales',
          label: t('SIDEBAR.HELP_CENTER.LOCALES'),
          activeOn: ['portals_locales_index'],
          to: accountScopedRoute('portals_index', {
            navigationPath: 'portals_locales_index',
          }),
        },
        {
          name: 'Help Center Settings',
          label: t('SIDEBAR.HELP_CENTER.SETTINGS'),
          activeOn: ['portals_settings_index'],
          to: accountScopedRoute('portals_index', {
            navigationPath: 'portals_settings_index',
          }),
        },
      ],
    },
    {
      name: 'Settings',
      label: t('SIDEBAR.SETTINGS'),
      icon: SIDEBAR_ICONS.settings,
      to: accountScopedRoute('settings_home'),
      activeOn: route.meta.settingsSection ? [route.name] : [],
    },
  ];
});

const menuSections = computed(() => {
  const itemsByName = new Map(
    menuItems.value.map(menuItem => [menuItem.name, menuItem])
  );
  const resolveItems = names =>
    names.map(name => itemsByName.get(name)).filter(Boolean);

  return [
    {
      name: 'overview',
      label: t('SIDEBAR.SECTIONS.MAIN'),
      items: resolveItems(['Overview', 'Internal Chat', 'Conversation']),
    },
    {
      name: 'relationship',
      label: t('SIDEBAR.SECTIONS.RELATIONSHIP'),
      items: resolveItems([
        'YesCRM',
        'Prospecting',
        'Calendar',
        'Contacts',
        'Companies',
      ]),
    },
    {
      name: 'management',
      label: t('SIDEBAR.SECTIONS.MANAGEMENT'),
      items: resolveItems(['Portals', 'Settings']),
    },
  ];
});
</script>

<template>
  <aside
    v-on-click-outside="[
      closeMobileSidebar,
      { ignore: ['#mobile-sidebar-launcher'] },
    ]"
    class="bg-n-solid-2 rtl:border-l ltr:border-r border-n-weak flex flex-col text-[13px] pb-1 fixed top-0 ltr:left-0 rtl:right-0 h-full z-40 transition-transform duration-200 ease-in-out md:static w-[200px] basis-[200px] md:flex-shrink-0 md:ltr:translate-x-0 md:rtl:-translate-x-0"
    :class="[
      {
        'shadow-lg md:shadow-none': isMobileSidebarOpen,
        'ltr:-translate-x-full rtl:translate-x-full': !isMobileSidebarOpen,
      },
    ]"
  >
    <section class="grid gap-2 mt-2 mb-4">
      <div class="flex justify-center items-center px-2 min-w-0 mb-2">
        <img
          v-if="globalConfig.logo"
          :src="globalConfig.logo"
          alt="Logo"
          class="h-8 w-auto max-w-full object-contain"
        />
        <Logo v-else class="size-6" />
      </div>
      <div class="flex gap-2 px-2">
        <RouterLink
          :to="{ name: 'search' }"
          class="flex gap-2 items-center px-2 py-1 w-full h-7 rounded-lg outline outline-1 outline-n-weak bg-n-solid-3 dark:bg-n-black/30"
        >
          <span class="flex-shrink-0 i-lucide-search size-4 text-n-slate-11" />
          <span class="flex-grow text-left">
            {{ t('COMBOBOX.SEARCH_PLACEHOLDER') }}
          </span>
          <span
            class="hidden tracking-wide pointer-events-none select-none text-n-slate-10"
          >
            {{ searchShortcut }}
          </span>
        </RouterLink>
        <ComposeConversation align-position="right">
          <template #trigger="{ toggle }">
            <Button
              icon="i-lucide-pen-line"
              color="slate"
              size="sm"
              class="!h-7 !bg-n-solid-3 dark:!bg-n-black/30 !outline-n-weak !text-n-slate-11"
              @click="toggle"
            />
          </template>
        </ComposeConversation>
      </div>
    </section>
    <nav class="grid overflow-y-scroll flex-grow gap-2 px-2 pb-5 no-scrollbar">
      <ul class="flex flex-col gap-1.5 m-0 list-none">
        <template v-for="section in menuSections" :key="section.name">
          <li
            v-if="section.label"
            class="px-2 pt-3 pb-1 text-[10px] font-semibold uppercase text-n-slate-9"
          >
            {{ section.label }}
          </li>
          <SidebarGroup
            v-for="item in section.items"
            :key="item.name"
            v-bind="item"
          />
        </template>
      </ul>
    </nav>
    <section
      class="flex flex-col flex-shrink-0 relative gap-1 justify-between items-center"
    >
      <div
        class="pointer-events-none absolute inset-x-0 -top-[31px] h-8 bg-gradient-to-t from-n-solid-2 to-transparent"
      />
      <SidebarChangelogCard
        v-if="isOnChatwootCloud && !isACustomBrandedInstance"
      />
      <div
        class="p-1 flex-shrink-0 flex w-full justify-between z-10 gap-2 items-center border-t border-n-weak shadow-[0px_-2px_4px_0px_rgba(27,28,29,0.02)]"
      >
        <SidebarProfileMenu />
      </div>
    </section>
  </aside>
</template>
